import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiPlus, FiRefreshCw, FiChevronLeft, FiMic, FiPlay, FiFileText, FiTrash2, FiCheck, FiClock, FiUserX,
  FiDownload, FiAlertTriangle, FiLock, FiUnlock, FiEdit2,
} from 'react-icons/fi';
import { meetingsAPI, meetingFlagsAPI, teamTasksAPI } from '../services/api';
import { useOpenParam } from '../utils/openParam';
import { useAuth } from '../contexts/AuthContext';
import './VisaCases.css';
import './Meetings.css';

// Team meetings (brainstorms/2026-10-17-foundation-enforcement.md, Q4/Q5). Every meeting lives here:
// the weekly Saturday 21:00 meeting is created automatically. The chair marks attendance live, uploads
// the WhatsApp call recording, checks Claude's draft minutes and approves them; action items become
// team tasks in one click.

const ATT_LABEL = { EXPECTED: 'Not marked', ON_TIME: 'On time', LATE: 'Late', NO_SHOW: 'No-show', EXCUSED: 'Absent (notified)' };
const ATT_TONE = { EXPECTED: '', ON_TIME: 'vc-status-approved', LATE: 'vc-badge-warn', NO_SHOW: 'vc-status-refused', EXCUSED: 'vc-status-open' };
const MIN_LABEL = { NONE: 'No minutes', DRAFT: 'Minutes in draft', APPROVED: 'Minutes approved' };
const MIN_TONE = { NONE: '', DRAFT: 'vc-badge-warn', APPROVED: 'vc-status-approved' };
const REC_LABEL = {
  UPLOADING: 'Upload not finished', QUEUED: 'Waiting to be processed', TRANSCRIBING: 'Transcribing the recording',
  DRAFTING: 'Drafting the minutes', READY: 'Draft minutes ready', TRANSCRIBED: 'Transcript ready', FAILED: 'Processing failed', DELETED: 'Recording deleted',
};
const PROCESSING = ['QUEUED', 'TRANSCRIBING', 'DRAFTING'];
const TASK_LABEL = { OPEN: 'open', IN_PROGRESS: 'in progress', AT_RISK: 'at risk', DONE: 'done', CANCELLED: 'cancelled' };
const FLAG_STATUS = { PENDING: 'Waiting for a decision', CONFIRMED: 'Level 1 reminder confirmed', DISMISSED: 'Dismissed' };
const AUDIO_TYPES = { m4a: 'audio/mp4', mp4: 'video/mp4', mp3: 'audio/mpeg', ogg: 'audio/ogg', opus: 'audio/ogg', wav: 'audio/wav', webm: 'audio/webm', aac: 'audio/aac', flac: 'audio/flac' };

const errText = (err, fallback) => {
  const d = err?.response?.data || {};
  if (typeof d === 'string') return fallback;
  return d.error || d.detail || Object.values(d).flat()[0] || fallback;
};
// Meetings run on Harare time, so times are shown in CAT whatever the viewer's computer is set to.
const TZ = 'Africa/Harare';
const fmtDateTime = (v) => (v ? `${new Date(v).toLocaleString(undefined, { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })} CAT` : '');
const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString(undefined, { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false }) : '');
const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const mb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const Meetings = () => {
  const [tab, setTab] = useState('upcoming');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  useOpenParam('meeting', (id) => setOpenId(Number(id)));
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    if (tab === 'flags') return;
    setLoading(true);
    setError('');
    try {
      const res = await meetingsAPI.list({ scope: tab, ordering: tab === 'upcoming' ? 'scheduled_at' : '-scheduled_at' });
      setList(res.data?.results ?? res.data ?? []);
    } catch (err) {
      setError(errText(err, 'Could not load meetings.'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    teamTasksAPI.people().then((r) => setPeople(r.data)).catch(() => setPeople([]));
  }, []);

  if (openId) {
    return (
      <MeetingDetail
        id={openId}
        people={people}
        onBack={() => { setOpenId(null); load(); }}
        onEdit={(m) => setForm({ meeting: m })}
        formOpen={form}
        closeForm={() => setForm(null)}
      />
    );
  }

  return (
    <div className="mt-page">
      <div className="vc-header">
        <div>
          <h1 className="mt-title">Meetings</h1>
          <p className="vc-muted">The weekly team meeting (Saturday 21:00, chaired by the Chairman) is added automatically. Record the call, upload it, and the minutes are drafted for the chair to check.</p>
        </div>
        <div className="vc-header-actions">
          <button type="button" className="vc-btn" onClick={load}><FiRefreshCw /> Refresh</button>
          <button type="button" className="vc-btn vc-btn-primary" onClick={() => setForm({ meeting: null })}><FiPlus /> Schedule meeting</button>
        </div>
      </div>

      <div className="vc-filters">
        <div className="vc-tabs">
          <button type="button" className={tab === 'upcoming' ? 'active' : ''} onClick={() => setTab('upcoming')}>Upcoming</button>
          <button type="button" className={tab === 'past' ? 'active' : ''} onClick={() => setTab('past')}>Past and minutes</button>
          <button type="button" className={tab === 'flags' ? 'active' : ''} onClick={() => setTab('flags')}>Attendance reviews</button>
        </div>
      </div>

      {error && <div className="vc-error">{error}</div>}

      {tab === 'flags' ? <FlagsPanel /> : loading ? (
        <div className="vc-empty">Loading meetings…</div>
      ) : list.length === 0 ? (
        <div className="vc-empty">{tab === 'upcoming' ? 'No upcoming meetings.' : 'No past meetings yet.'}</div>
      ) : (
        <div className="vc-table">
          {list.map((m) => (
            <button type="button" key={m.id} className="vc-rowcard" onClick={() => setOpenId(m.id)}>
              <div>
                <div className="mt-row-title">{m.title}</div>
                <div className="vc-muted">{fmtDateTime(m.scheduled_at)}{m.chair_name ? ` · Chair: ${m.chair_name}` : ''}</div>
              </div>
              <div className="vc-rowcard-side">
                {m.kind === 'TEAM_WEEKLY' && <span className="vc-badge vc-badge-info">Weekly</span>}
                {m.kind === 'MONTHLY_PLANNING' && <span className="vc-badge vc-badge-info">Planning</span>}
                {m.my_status && m.my_status !== 'EXPECTED' && (
                  <span className={`vc-status ${ATT_TONE[m.my_status]}`}>You: {ATT_LABEL[m.my_status]}</span>
                )}
                {m.is_past && <span className="vc-muted">{m.marked}/{m.invited} marked</span>}
                {m.recording_status && m.recording_status !== 'DELETED' && (
                  <span className={`vc-badge ${m.recording_status === 'FAILED' ? 'vc-badge-bad' : 'vc-badge-info'}`}><FiMic /> {REC_LABEL[m.recording_status]}</span>
                )}
                {m.is_past && <span className={`vc-status ${MIN_TONE[m.minutes_status]}`}>{MIN_LABEL[m.minutes_status]}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {form && (
        <MeetingForm
          meeting={form.meeting}
          people={people}
          onClose={() => setForm(null)}
          onSaved={(m) => { setForm(null); if (!form.meeting) setOpenId(m.id); else load(); }}
        />
      )}
    </div>
  );
};

// --- create / edit ---------------------------------------------------------------------------

const MeetingForm = ({ meeting, people, onClose, onSaved }) => {
  const { user } = useAuth();
  const invitedIds = meeting ? meeting.attendance.map((a) => a.user) : people.map((p) => p.id);
  const [f, setF] = useState({
    title: meeting?.title || '',
    scheduled_at: toLocalInput(meeting?.scheduled_at),
    end_time: toLocalInput(meeting?.end_time),
    location_or_link: meeting?.location_or_link || 'WhatsApp group call',
    description: meeting?.description || '',
    chair: meeting?.chair || user?.id || '',
    invitees: invitedIds,
    planning: meeting?.kind === 'MONTHLY_PLANNING',
  });
  const isWeekly = meeting?.kind === 'TEAM_WEEKLY';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (id) => set('invitees', f.invitees.includes(id) ? f.invitees.filter((x) => x !== id) : [...f.invitees, id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      title: f.title, scheduled_at: fromLocalInput(f.scheduled_at), end_time: fromLocalInput(f.end_time),
      location_or_link: f.location_or_link, description: f.description, chair: f.chair || null, invitee_ids: f.invitees,
      ...(isWeekly ? {} : { kind: f.planning ? 'MONTHLY_PLANNING' : 'OTHER' }),
    };
    try {
      const res = meeting ? await meetingsAPI.update(meeting.id, payload) : await meetingsAPI.create(payload);
      onSaved(res.data);
    } catch (err) {
      setError(errText(err, 'Could not save the meeting.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vc-overlay" onClick={onClose}>
      <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vc-modal-head"><h2>{meeting ? 'Edit meeting' : 'Schedule meeting'}</h2></div>
        <form className="vc-form" onSubmit={save}>
          <label className="vc-label">Title
            <input value={f.title} onChange={(e) => set('title', e.target.value)} required />
          </label>
          <div className="vc-row">
            <label className="vc-label">Starts (your computer's time)
              <input type="datetime-local" value={f.scheduled_at} onChange={(e) => set('scheduled_at', e.target.value)} required />
            </label>
            <label className="vc-label">Ends (your computer's time)
              <input type="datetime-local" value={f.end_time} onChange={(e) => set('end_time', e.target.value)} />
            </label>
          </div>
          <div className="vc-row">
            <label className="vc-label">Where
              <input value={f.location_or_link} onChange={(e) => set('location_or_link', e.target.value)} />
            </label>
            <label className="vc-label">Chair
              <select value={f.chair} onChange={(e) => set('chair', Number(e.target.value) || '')}>
                <option value="">Me</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          </div>
          {!isWeekly && (
            <label className="vc-check">
              <input type="checkbox" checked={f.planning} onChange={(e) => set('planning', e.target.checked)} />
              {' '}Monthly planning session (counts for KPI CEO2 once its minutes are approved)
            </label>
          )}
          <label className="vc-label">Agenda
            <textarea className="vc-note" rows={5} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="One item per line" />
          </label>
          <div className="vc-label">Invited
            <div className="mt-invitees">
              {people.map((p) => (
                <label key={p.id} className="vc-check">
                  <input type="checkbox" checked={f.invitees.includes(p.id)} onChange={() => toggle(p.id)} /> {p.name}
                </label>
              ))}
            </div>
          </div>
          {error && <div className="vc-error">{error}</div>}
          <div className="vc-modal-actions">
            <button type="button" className="vc-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="vc-btn vc-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- detail ----------------------------------------------------------------------------------

const MeetingDetail = ({ id, people, onBack, onEdit, formOpen, closeForm }) => {
  const { isAdmin } = useAuth();
  const [m, setM] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await meetingsAPI.get(id);
      setM(res.data);
      setError('');
    } catch (err) {
      setError(errText(err, 'Could not load the meeting.'));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // While Claude works on the recording, check every 6 seconds.
  const processing = m?.recording && PROCESSING.includes(m.recording.status);
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [processing, load]);

  const run = async (fn, fallback) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      if (res?.data?.id === id) setM(res.data); else await load();
      return true;
    } catch (err) {
      setError(errText(err, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!m) {
    return (
      <div className="mt-page">
        <button type="button" className="vc-link vc-back" onClick={onBack}><FiChevronLeft /> All meetings</button>
        {error ? <div className="vc-error">{error}</div> : <div className="vc-empty">Loading…</div>}
      </div>
    );
  }

  const perms = m.permissions;
  const started = new Date(m.scheduled_at) <= new Date();

  return (
    <div className="mt-page">
      <button type="button" className="vc-link vc-back" onClick={onBack}><FiChevronLeft /> All meetings</button>
      <div className="vc-detail-head">
        <div>
          <h1 className="mt-title">{m.title}</h1>
          <div className="vc-muted">{fmtDateTime(m.scheduled_at)}{m.end_time ? ` – ${fmtTime(m.end_time)}` : ''} · {m.location_or_link || 'No link'}</div>
          <div className="vc-muted">Chair: {m.chair_name || 'not set'}</div>
        </div>
        <div className="vc-detail-side">
          <span className={`vc-status ${MIN_TONE[m.minutes_status]}`}>{MIN_LABEL[m.minutes_status]}</span>
          {perms.can_edit && <button type="button" className="vc-btn vc-btn-sm" onClick={() => onEdit(m)}><FiEdit2 /> Edit</button>}
        </div>
      </div>
      {error && <div className="vc-error">{error}</div>}

      <div className="vc-panel">
        <div className="vc-panel-head">
          <h3>Agenda</h3>
          {!started && <span className="vc-muted">Due by {fmtDateTime(m.agenda_due_at)}</span>}
        </div>
        {m.description ? <div className="mt-pre">{m.description}</div> : <p className="vc-muted">No agenda yet.</p>}
      </div>

      {perms.is_invited && !m.is_past && <NoticePanel m={m} run={run} busy={busy} />}
      <AttendancePanel m={m} run={run} busy={busy} />
      <RecordingPanel m={m} run={run} busy={busy} reload={load} setError={setError} />
      <MinutesPanel m={m} run={run} busy={busy} isAdmin={isAdmin()} />
      <ItemsPanel m={m} run={run} busy={busy} people={people} />

      {formOpen && (
        <MeetingForm meeting={formOpen.meeting} people={people} onClose={closeForm}
          onSaved={(saved) => { closeForm(); setM(saved); }} />
      )}
    </div>
  );
};

const NoticePanel = ({ m, run, busy }) => {
  const { user } = useAuth();
  const [kind, setKind] = useState(null);
  const [reason, setReason] = useState('');
  const [eta, setEta] = useState('');
  const mine = m.notices.filter((n) => n.user === user?.id);

  const send = async () => {
    const ok = await run(() => meetingsAPI.giveNotice(m.id, { kind, reason, eta: fromLocalInput(eta) }), 'Could not send.');
    if (ok) { setKind(null); setReason(''); setEta(''); }
  };
  const waText = (n) => encodeURIComponent(
    `${n.kind === 'LATE' ? "I'll be late for" : "I'll be absent from"} "${m.title}" (${fmtDateTime(m.scheduled_at)}). ${n.reason}`
    + (n.eta ? ` I expect to join at ${fmtTime(n.eta)}.` : ''),
  );

  return (
    <div className="vc-panel">
      <div className="vc-panel-head">
        <h3>Can't be on time?</h3>
        {!kind && (
          <div className="vc-share-actions">
            <button type="button" className="vc-btn vc-btn-sm" onClick={() => setKind('LATE')}><FiClock /> I'll be late</button>
            <button type="button" className="vc-btn vc-btn-sm" onClick={() => setKind('ABSENT')}><FiUserX /> I'll be absent</button>
          </div>
        )}
      </div>
      <p className="vc-muted">Notice counts only if it is sent before the meeting starts. It is timestamped here; share it in the WhatsApp group too.</p>
      {kind && (
        <div className="vc-item-form">
          <label className="vc-label">Reason
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={kind === 'LATE' ? 'e.g. Still at the VFS centre' : 'e.g. Clinic appointment'} />
          </label>
          {kind === 'LATE' && (
            <label className="vc-label">Expected to join at
              <input type="datetime-local" value={eta} onChange={(e) => setEta(e.target.value)} />
            </label>
          )}
          <div className="vc-item-form-actions">
            <button type="button" className="vc-btn" onClick={() => setKind(null)}>Cancel</button>
            <button type="button" className="vc-btn vc-btn-primary" disabled={busy || !reason.trim()} onClick={send}>Send notice</button>
          </div>
        </div>
      )}
      {mine.map((n) => (
        <div key={n.id} className="mt-notice">
          <span>{n.kind === 'LATE' ? "Late" : 'Absent'} notice sent {fmtDateTime(n.created_at)}{n.before_start ? '' : ' (after the start: does not count)'}</span>
          <a className="vc-btn vc-btn-sm vc-btn-wa" href={`https://wa.me/?text=${waText(n)}`} target="_blank" rel="noopener noreferrer">Share on WhatsApp</a>
        </div>
      ))}
    </div>
  );
};

const AttendancePanel = ({ m, run, busy }) => {
  const [timeFor, setTimeFor] = useState(null);
  const [arrival, setArrival] = useState('');
  const canRun = m.permissions.can_run;
  const started = new Date(m.scheduled_at).getTime() - 30 * 60 * 1000 <= Date.now();
  const graceOver = new Date(m.grace_until) <= new Date();
  const mark = (userId, action, arrivedAt) => run(
    () => meetingsAPI.markAttendance(m.id, { user: userId, action, arrived_at: arrivedAt || undefined }),
    'Could not mark attendance.',
  );
  const notices = (userId) => m.notices.filter((n) => n.user === userId);

  return (
    <div className="vc-panel">
      <div className="vc-panel-head">
        <h3>Attendance</h3>
        <span className="vc-muted">{m.marked}/{m.invited} marked · 10-minute grace until {fmtTime(m.grace_until)} CAT</span>
      </div>
      {canRun && !started && <p className="vc-muted">You can mark attendance from 30 minutes before the start.</p>}
      <ul className="vc-items">
        {m.attendance.map((a) => (
          <li key={a.id} className="vc-item">
            <div className="vc-item-main">
              <div className="vc-item-title">
                <strong>{a.name}</strong>
                <span className={`vc-status ${ATT_TONE[a.status]}`}>{ATT_LABEL[a.status]}</span>
                {a.arrived_at && <span className="vc-muted">arrived {fmtTime(a.arrived_at)}</span>}
                {a.notified && <span className="vc-badge vc-badge-info">Gave notice</span>}
              </div>
              {notices(a.user).map((n) => (
                <div key={n.id} className="vc-item-meta">
                  {n.kind === 'LATE' ? 'Late' : 'Absent'} · sent {fmtDateTime(n.created_at)}{n.before_start ? '' : ' (after start)'}
                  {n.eta ? ` · ETA ${fmtTime(n.eta)}` : ''}{n.reason ? ` · ${n.reason}` : ''}
                </div>
              ))}
            </div>
            {canRun && started && (
              <div className="vc-item-actions">
                <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => mark(a.user, 'arrived')}><FiCheck /> Arrived now</button>
                <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => { setTimeFor(a.user); setArrival(toLocalInput(m.scheduled_at)); }}>Arrived at…</button>
                <button type="button" className="vc-btn vc-btn-sm" disabled={busy || !graceOver} title={graceOver ? '' : 'Available once the grace period is over'} onClick={() => mark(a.user, 'absent')}><FiUserX /> No-show</button>
                {a.status !== 'EXPECTED' && <button type="button" className="vc-link" disabled={busy} onClick={() => mark(a.user, 'reset')}>Undo</button>}
              </div>
            )}
            {timeFor === a.user && (
              <div className="vc-item-form">
                <label className="vc-label">Arrival time
                  <input type="datetime-local" value={arrival} onChange={(e) => setArrival(e.target.value)} />
                </label>
                <div className="vc-item-form-actions">
                  <button type="button" className="vc-btn" onClick={() => setTimeFor(null)}>Cancel</button>
                  <button type="button" className="vc-btn vc-btn-primary" disabled={busy}
                    onClick={async () => { if (await mark(a.user, 'arrived', fromLocalInput(arrival))) setTimeFor(null); }}>Save</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const RecordingPanel = ({ m, run, busy, reload, setError }) => {
  const rec = m.recording;
  const perms = m.permissions;
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [progress, setProgress] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const live = rec && rec.status !== 'DELETED';
  const canUpload = perms.can_run && !m.minutes_status.includes('APPROVED') && (!rec || !PROCESSING.includes(rec.status));

  const upload = async () => {
    setError('');
    setProgress(0);
    try {
      await meetingsAPI.uploadRecording(m.id, file, { onProgress: setProgress });
      setFile(null);
      setConsent(false);
      if (fileInput.current) fileInput.current.value = '';
      await reload();
    } catch (err) {
      setError(errText(err, 'The upload failed. Check the connection and try again: it continues where it stopped.'));
    } finally {
      setProgress(null);
    }
  };

  const play = async () => {
    try {
      const res = await meetingsAPI.recordingAudio(m.id);
      const ext = (rec.original_name.split('.').pop() || '').toLowerCase();
      setAudioUrl(URL.createObjectURL(new Blob([res.data], { type: AUDIO_TYPES[ext] || 'audio/mp4' })));
    } catch (err) {
      setError('Could not open the recording.');
    }
  };

  const showTranscript = async () => {
    try {
      const res = await meetingsAPI.recordingTranscript(m.id);
      setTranscript(res.data);
    } catch (err) {
      setError('Could not open the transcript.');
    }
  };

  if (!perms.can_run && !perms.can_access_recording) {
    return live ? (
      <div className="vc-panel"><h3>Recording</h3><p className="vc-muted"><FiMic /> {REC_LABEL[rec.status]}</p></div>
    ) : null;
  }

  return (
    <div className="vc-panel">
      <div className="vc-panel-head">
        <h3>Recording</h3>
        {live && <span className={`vc-badge ${rec.status === 'FAILED' ? 'vc-badge-bad' : 'vc-badge-info'}`}>{PROCESSING.includes(rec.status) && <FiRefreshCw className="mt-spin" />} {REC_LABEL[rec.status]}</span>}
      </div>

      {live && (
        <div className="mt-rec">
          <div className="vc-muted">
            {rec.original_name} · {mb(rec.size || rec.expected_size)} · uploaded by {rec.uploaded_by_name} {fmtDateTime(rec.uploaded_at || rec.upload_started_at)}
            {' · '}recording announced (confirmed {fmtDateTime(rec.consent_confirmed_at)})
          </div>
          {rec.status === 'FAILED' && (
            <div className="vc-route vc-route-bad"><FiAlertTriangle /> {rec.error}
              {perms.can_run && (
                <div className="vc-share-actions">
                  <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => run(() => meetingsAPI.retryRecording(m.id), 'Could not retry.')}>Try again</button>
                </div>
              )}
            </div>
          )}
          {PROCESSING.includes(rec.status) && <p className="vc-muted">This takes a few minutes for a one-hour call. You can leave this page.</p>}
          {rec.status === 'TRANSCRIBED' && (
            <div className="vc-route vc-route-info">
              The recording has been transcribed. Automatic draft minutes are not switched on, so the chair writes
              the minutes below from the transcript{perms.can_access_recording ? ' (Show transcript)' : ''}, then approves them.
            </div>
          )}
          {rec.delete_after && <p className="vc-muted">The audio and transcript will be deleted on {fmtDate(rec.delete_after)}. The approved minutes stay.</p>}
          {perms.can_access_recording && rec.status !== 'UPLOADING' && (
            <div className="vc-share-actions">
              {!audioUrl && <button type="button" className="vc-btn vc-btn-sm" onClick={play}><FiPlay /> Play recording</button>}
              {rec.has_transcript && !transcript && <button type="button" className="vc-btn vc-btn-sm" onClick={showTranscript}><FiFileText /> Show transcript</button>}
              {rec.status === 'READY' && perms.can_run && m.minutes_status !== 'APPROVED' && (
                <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => run(() => meetingsAPI.retryRecording(m.id), 'Could not redraft.')}><FiRefreshCw /> Redraft minutes</button>
              )}
              {!PROCESSING.includes(rec.status) && (
                <button type="button" className="vc-btn vc-btn-sm" disabled={busy}
                  onClick={() => window.confirm('Delete the audio and transcript now? The minutes stay.') && run(() => meetingsAPI.deleteRecording(m.id), 'Could not delete.')}>
                  <FiTrash2 /> Delete recording now
                </button>
              )}
            </div>
          )}
          {audioUrl && <audio className="mt-audio" controls src={audioUrl}>Your browser cannot play this file.</audio>}
          {transcript && <div className="mt-pre mt-transcript">{transcript}</div>}
        </div>
      )}

      {canUpload && (
        <div className="mt-upload">
          {live && <p className="vc-muted">Uploading a new file replaces the current recording.</p>}
          <details className="mt-howto">
            <summary>How to record a WhatsApp call</summary>
            <ul>
              <li><strong>Best:</strong> join the call on WhatsApp Desktop on a laptop and record with OBS Studio (free). It captures everyone, not only your voice.</li>
              <li><strong>Fallback:</strong> put your phone on speaker and record with a voice recorder app on a second phone or laptop.</li>
              <li>Most Android phones cannot record call audio from the other side, so the phone's own screen recorder usually picks up only your voice.</li>
              <li>At the start of the call, say: "This meeting is being recorded for the minutes."</li>
            </ul>
          </details>
          <input ref={fileInput} type="file" accept="audio/*,video/*,.m4a,.mp3,.mp4,.ogg,.opus,.wav,.webm,.aac,.mkv,.flac,.3gp,.amr"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <label className="vc-check mt-consent">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            I told everyone at the start of the call that it was being recorded, and nobody objected.
          </label>
          {progress !== null && (
            <div className="vc-progress"><div className="vc-progress-bar mt-progress"><span style={{ width: `${Math.round(progress * 100)}%` }} /></div><span className="vc-progress-num">{Math.round(progress * 100)}%</span></div>
          )}
          <div>
            <button type="button" className="vc-btn vc-btn-primary" disabled={!file || !consent || progress !== null} onClick={upload}>
              <FiMic /> {progress !== null ? 'Uploading…' : 'Upload and draft minutes'}
            </button>
            {file && <span className="vc-muted"> {file.name} · {mb(file.size)}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const MinutesPanel = ({ m, run, busy, isAdmin }) => {
  const perms = m.permissions;
  const locked = m.minutes_status === 'APPROVED';
  const editable = perms.can_run && !locked;
  const [text, setText] = useState(m.minutes_notes || '');
  const [fileToUpload, setFileToUpload] = useState(null);
  useEffect(() => { setText(m.minutes_notes || ''); }, [m.minutes_notes]);
  const dirty = (m.minutes_notes || '') !== text;
  const draft = m.recording?.draft_text;

  const download = async () => {
    const res = await meetingsAPI.downloadMinutes(m.id);
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = (m.minutes_file || 'minutes').split('/').pop();
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="vc-panel">
      <div className="vc-panel-head">
        <h3>Minutes</h3>
        {locked ? (
          <span className="vc-muted"><FiLock /> Approved by {m.minutes_approved_by_name} {fmtDateTime(m.minutes_approved_at)}</span>
        ) : m.minutes_status === 'DRAFT' && <span className="vc-badge vc-badge-warn">Draft: the chair checks and approves</span>}
      </div>
      {editable ? (
        <>
          {draft && draft !== text && (
            <div className="vc-route vc-route-info">
              A draft from the recording is ready and differs from the text below.
              <div className="vc-share-actions">
                <button type="button" className="vc-btn vc-btn-sm" disabled={busy}
                  onClick={() => (!text.trim() || window.confirm('Replace the minutes below with the draft?')) && run(() => meetingsAPI.useDraft(m.id), 'Could not use the draft.')}>
                  Use the draft
                </button>
              </div>
            </div>
          )}
          <textarea className="vc-note mt-minutes" rows={16} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Upload the recording to get a draft, or type the minutes here." />
          <div className="vc-share-actions">
            <button type="button" className="vc-btn" disabled={busy || !dirty} onClick={() => run(() => meetingsAPI.update(m.id, { minutes_notes: text }), 'Could not save.')}>Save</button>
            <button type="button" className="vc-btn vc-btn-primary" disabled={busy || dirty || !text.trim()} title={dirty ? 'Save first' : ''}
              onClick={() => window.confirm('Approve and lock these minutes? The recording will be deleted 30 days from now.') && run(() => meetingsAPI.approve(m.id), 'Could not approve.')}>
              <FiCheck /> Approve minutes
            </button>
          </div>
        </>
      ) : (
        m.minutes_notes ? <div className="mt-pre">{m.minutes_notes}</div> : <p className="vc-muted">No minutes yet.</p>
      )}
      {locked && isAdmin && (
        <button type="button" className="vc-link" disabled={busy} onClick={() => window.confirm('Reopen the approved minutes for correction?') && run(() => meetingsAPI.reopenMinutes(m.id), 'Could not reopen.')}>
          <FiUnlock /> Reopen for correction
        </button>
      )}
      <div className="mt-file">
        {m.minutes_file && <button type="button" className="vc-link" onClick={download}><FiDownload /> Download attached minutes file</button>}
        {editable && (
          <span className="mt-file-upload">
            <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={(e) => setFileToUpload(e.target.files?.[0] || null)} />
            <button type="button" className="vc-btn vc-btn-sm" disabled={!fileToUpload || busy} onClick={() => run(() => meetingsAPI.uploadMinutesFile(m.id, fileToUpload), 'Upload failed.')}>Attach file</button>
          </span>
        )}
      </div>
    </div>
  );
};

const ItemsPanel = ({ m, run, busy, people }) => {
  const canRun = m.permissions.can_run;
  const locked = m.minutes_status === 'APPROVED';
  const [adding, setAdding] = useState(null);
  const decisions = m.items.filter((i) => i.kind === 'DECISION');
  const actions = m.items.filter((i) => i.kind === 'ACTION');
  const edit = (item, data) => run(() => meetingsAPI.updateItem(m.id, item.id, data), 'Could not save.');

  if (!m.items.length && !canRun) return null;

  return (
    <div className="vc-panel">
      <div className="vc-panel-head">
        <h3>Decisions and action items</h3>
        {canRun && !locked && !adding && (
          <div className="vc-share-actions">
            <button type="button" className="vc-btn vc-btn-sm" onClick={() => setAdding({ kind: 'DECISION', text: '' })}><FiPlus /> Decision</button>
            <button type="button" className="vc-btn vc-btn-sm" onClick={() => setAdding({ kind: 'ACTION', text: '', owner: '', due_date: '' })}><FiPlus /> Action item</button>
          </div>
        )}
      </div>
      {adding && (
        <div className="vc-item-form">
          <label className="vc-label">{adding.kind === 'DECISION' ? 'Decision' : 'What will be done'}
            <input value={adding.text} onChange={(e) => setAdding({ ...adding, text: e.target.value })} />
          </label>
          {adding.kind === 'ACTION' && (
            <div className="vc-row">
              <label className="vc-label">Who
                <select value={adding.owner} onChange={(e) => setAdding({ ...adding, owner: e.target.value })}>
                  <option value="">Not decided</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="vc-label">Due
                <input type="date" value={adding.due_date} onChange={(e) => setAdding({ ...adding, due_date: e.target.value })} />
              </label>
            </div>
          )}
          <div className="vc-item-form-actions">
            <button type="button" className="vc-btn" onClick={() => setAdding(null)}>Cancel</button>
            <button type="button" className="vc-btn vc-btn-primary" disabled={busy || !adding.text.trim()}
              onClick={async () => {
                const data = { kind: adding.kind, text: adding.text, owner: adding.owner || null, due_date: adding.due_date || null };
                if (await run(() => meetingsAPI.addItem(m.id, data), 'Could not add.')) setAdding(null);
              }}>Add</button>
          </div>
        </div>
      )}
      {decisions.length > 0 && (
        <>
          <h4 className="mt-sub">Decisions</h4>
          <ol className="mt-decisions">
            {decisions.map((d) => (
              <li key={d.id}>{d.text}{canRun && !locked && (
                <button type="button" className="vc-link mt-del" onClick={() => run(() => meetingsAPI.deleteItem(m.id, d.id), 'Could not remove.')}>Remove</button>
              )}</li>
            ))}
          </ol>
        </>
      )}
      {actions.length > 0 && <h4 className="mt-sub">Action items</h4>}
      <ul className="vc-items">
        {actions.map((a) => (
          <li key={a.id} className="vc-item">
            <div className="vc-item-main">
              <div className="vc-item-title">{a.text}{a.from_ai && <span className="vc-tag">from recording</span>}</div>
              {!a.owner && a.owner_name && <div className="vc-item-meta">Heard as: {a.owner_name}</div>}
            </div>
            <div className="vc-item-actions">
              {a.task ? (
                <Link className="vc-badge vc-badge-ok" to={`/team-tasks?task=${a.task}`}>Task: {TASK_LABEL[a.task_status] || a.task_status}</Link>
              ) : canRun ? (
                <>
                  <select className="mt-select" value={a.owner || ''} disabled={busy || locked} onChange={(e) => edit(a, { owner: e.target.value || null })}>
                    <option value="">Who?</option>
                    {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input className="mt-select" type="date" value={a.due_date || ''} disabled={busy || locked} onChange={(e) => edit(a, { due_date: e.target.value || null })} />
                  <button type="button" className="vc-btn vc-btn-sm vc-btn-primary" disabled={busy || !a.owner || !a.due_date}
                    title={!a.owner || !a.due_date ? 'Pick who and when first' : ''}
                    onClick={() => run(() => meetingsAPI.makeTask(m.id, a.id), 'Could not create the task.')}>Make task</button>
                  {!locked && <button type="button" className="vc-icon-btn" title="Remove" onClick={() => run(() => meetingsAPI.deleteItem(m.id, a.id), 'Could not remove.')}><FiTrash2 /></button>}
                </>
              ) : (
                <span className="vc-muted">{a.owner_display || 'No owner'}{a.due_date ? ` · ${fmtDate(a.due_date)}` : ''}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
      {!m.items.length && <p className="vc-muted">Decisions and action items from the recording appear here.</p>}
    </div>
  );
};

// --- attendance flags --------------------------------------------------------------------------

const FlagsPanel = () => {
  const [flags, setFlags] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    meetingFlagsAPI.list().then((r) => setFlags(r.data?.results ?? r.data)).catch((err) => setError(errText(err, 'Could not load.')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (f, decision) => {
    const note = window.prompt(decision === 'CONFIRMED' ? 'Note for the Level 1 reminder (shown to the person):' : 'Why is it dismissed?') ;
    if (note === null) return;
    try {
      await meetingFlagsAPI.decide(f.id, decision, note);
      load();
    } catch (err) {
      setError(errText(err, 'Could not save the decision.'));
    }
  };

  if (!flags) return error ? <div className="vc-error">{error}</div> : <div className="vc-empty">Loading…</div>;
  return (
    <>
      <p className="vc-muted">Three lates without notice in a month, or a no-show without notice, opens a review. Nothing happens automatically: the CEO or Chairman confirms a Level 1 reminder or dismisses it.</p>
      {error && <div className="vc-error">{error}</div>}
      {flags.length === 0 ? <div className="vc-empty">No attendance reviews.</div> : (
        <div className="vc-table">
          {flags.map((f) => (
            <div key={f.id} className="vc-rowcard mt-flag">
              <div>
                <div className="mt-row-title">{f.name} · {f.kind === 'NO_SHOW' ? 'No-show without notice' : '3 lates without notice'}</div>
                <div className="vc-muted">{f.details}</div>
                {f.decision_note && <div className="vc-muted">Note: {f.decision_note}</div>}
              </div>
              <div className="vc-rowcard-side">
                <span className={`vc-status ${f.status === 'PENDING' ? 'vc-badge-warn' : f.status === 'CONFIRMED' ? 'vc-status-refused' : ''}`}>{FLAG_STATUS[f.status]}</span>
                {f.decided_by_name && <span className="vc-muted">{f.decided_by_name}</span>}
                {f.can_decide && (
                  <>
                    <button type="button" className="vc-btn vc-btn-sm" onClick={() => decide(f, 'DISMISSED')}>Dismiss</button>
                    <button type="button" className="vc-btn vc-btn-sm vc-btn-primary" onClick={() => decide(f, 'CONFIRMED')}>Confirm reminder</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default Meetings;
